import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Skeleton,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AlertMessagesContext } from 'react-alert-messages';
import styles from './styles.module.css';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import PurchaseService from 'services/PurchaseService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { Link, useHistory, useParams } from 'react-router-dom';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ERROR_CODES, ROUTES, SUBSCRIPTION_TYPE } from 'const';
import EditIcon from '@mui/icons-material/Edit';
import VendorServices from 'services/VendorServices';
import ShopSettingsService from 'services/ShopSettingsService';
import CustomMenuItem from 'components/utils/CustomComponents/CustomMenuItem';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import NewVendorDialog from 'components/popups/NewVendorDialog';
import ConfirmationDialog from 'components/popups/ConfirmationDialog';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import { ShopContext } from 'Context/ShopContext';
import { InventoryContext } from 'Context/InventoryContext';
import ShopsHelper from 'helpers/ShopsHelper';
import NumberField from 'components/library/NumberField';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { WarehouseContext } from 'Context/WarehouseContext';

function AddPurchase() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { products: items, getProducts, setProducts, getStockBatches } = useContext(InventoryContext);
  const { warehouses, getWarehouses, getWarehouseProducts } = useContext(WarehouseContext);

  const { id } = useParams();
  const history = useHistory();
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [editData, setEditData] = useState({});
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [item, setItem] = useState(null);
  const [billDate, setBillDate] = useState(new Date());
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [itemPlu, setItemPlu] = useState('');
  const [itemName, setItemName] = useState('');
  const [cost, setCost] = useState(0);
  const [price, setPrice] = useState(0);
  const [profitMargin, setProfitMargin] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [tax, setTax] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [paidAmount, setPaidAmount] = useState();
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowAddVendonDialog, setShowAddVendonDialog] = useState(false);
  const [vendorNameOld, setVendorNameOld] = useState('');
  const [stock, setStock] = useState(0);
  const [prevStock, setPrevStock] = useState(0);
  const [vendor, setVendor] = useState(null);
  const [warehouse, setWarehouse] = useState('');
  const [searchText, setSearchText] = useState('');
  const [batchName, setBatchName] = useState('');
  const [editPurchaseItemId, setEditPurchaseItemId] = useState(null);
  const [isShowForceUpdateDialog, setShowForceUpdateDialog] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);

  const pluInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const costInputRef = useRef(null);
  const marginInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const qtyInputRef = useRef(null);
  const taxPercentInputRef = useRef(null);
  const taxAmountInputRef = useRef(null);
  const totalInputRef = useRef(null);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const featureValidity = shop?.customer_support_validity;

  const onCreateVendor = () => {
    getVendors();
    setShowAddVendonDialog(false);
  };

  const openCreateVendorDialog = () => {
    setShowAddVendonDialog(true);
  };
  const closeCreateVendorDialog = () => {
    setShowAddVendonDialog(false);
  };

  const getVendors = useCallback(async () => {
    const vedors = await VendorServices.getVendors();
    setVendors(vedors);
  }, []);

  const [paymentModes, setPaymentModes] = useState(null);

  const getPaymentModes = async () => {
    const resp = await ShopSettingsService.getPaymentModes();
    setPaymentModes(
      (resp || []).map((mode) => ({ mode_id: mode.id, name: mode.name, amount: 0 }))
    );
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingIndicator(true);
      await Promise.all([getVendors(), getWarehouses(), getPaymentModes()]);
      setLoadingIndicator(false);
    };
    fetchInitialData();
  }, [getVendors, getWarehouses]);

  useEffect(() => {
    if (!featureIsExpired(shop?.warehouse_validity) && shop?.config?.is_warehouse_enabled) {
      if (warehouses?.length > 0 && !editData?.id && warehouse === '') {
        if (shop?.config?.default_warehouse) {
          const defaultWh = warehouses.find((w) => w.id === shop.config.default_warehouse);
          if (defaultWh) {
            setWarehouse(defaultWh.id);
          } else {
            setWarehouse(warehouses[0].id);
          }
        } else {
          setWarehouse(warehouses[0].id);
        }
      }
    }
  }, [warehouses, shop, editData, warehouse]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const getPurchaseData = async () => {
      const purchase = await PurchaseService.getPurchase(id);
      setEditData(purchase);
      const paymentDate =
        purchase.payment_date && purchase.payment_date !== '' ? purchase.payment_date : purchase.bill_date;
      setInvoiceNumber(purchase.invoice_no);
      setBillDate(DateTimeHelpers.convertDateToMDY(purchase.bill_date));
      setPaymentDate(DateTimeHelpers.convertDateToMDY(paymentDate));

      const pDate = purchase.date || purchase.purchase_date;
      if (pDate && typeof pDate === 'string' && pDate.includes('/')) {
        const [dStr, tStr] = pDate.split(' ');
        const [d, m, y] = dStr.split('/');
        if (tStr) {
          const [hh, mm, ss] = tStr.split(':');
          setPurchaseDate(new Date(y, m - 1, d, hh, mm, ss || 0));
        } else {
          setPurchaseDate(new Date(y, m - 1, d));
        }
      } else {
        setPurchaseDate(new Date(pDate));
      }
      const processedItems = (purchase.items || []).map((pi) => {
        let updatedItem = pi?.item;
        if (updatedItem?.id) {
          const invItem = items.find((p) => p.id === updatedItem.id);
          if (invItem) {
            updatedItem = invItem;
          }
        }
        return {
          ...pi,
          item: updatedItem,
        };
      });
      setPurchasedItems(processedItems);
      setVendorNameOld(purchase.vendor_name_old);
      setPaidAmount(purchase.paid_amount);
      setVendor(purchase.vendor ?? null);
      if (purchase.warehouse !== undefined && purchase.warehouse !== null) {
        setWarehouse(typeof purchase.warehouse === 'object' ? purchase.warehouse.id : purchase.warehouse);
      } else if (purchase.warehouse_id !== undefined && purchase.warehouse_id !== null) {
        setWarehouse(purchase.warehouse_id);
      }
    };
    getPurchaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) {
      setBatchName(ShopsHelper.generateBatchName(purchaseDate, vendor));
    }
  }, [vendor, id, purchaseDate]);

  const resetItemFields = () => {
    setItemName('');
    setItemPlu('');
    setCost('');
    setPrice('');
    setProfitMargin('');
    setQuantity(1);
    setTax('');
    setTotalAmount('');
    setItem(null);
    setStock('');
    setTaxPercentage('');
    setEditPurchaseItemId(null);
  };

  const handleChangePayable = (val) => {
    if (quantity === 0 || item === null) return;

    const numVal = Number(val || 0);
    const numQty = Number(quantity || 1);
    let rawCost = 0;
    let newTax = Number(tax || 0);

    if (taxPercentage !== '' && taxPercentage !== null && !isNaN(Number(taxPercentage))) {
      const taxPercent = Number(taxPercentage);
      const base = numVal / (1 + taxPercent / 100);
      rawCost = base / numQty;
      newTax = numVal - base;
      setTax(newTax);
    } else {
      rawCost = (numVal - newTax) / numQty;
      const base = rawCost * numQty;
      const calculatedTaxPercentage = base > 0 ? (newTax / base) * 100 : 0;
      setTaxPercentage(calculatedTaxPercentage);
    }

    setTotalAmount(val);
    setCost(rawCost);

    const marginVal = profitMargin !== '' ? parseFloat(profitMargin) : (item && parseFloat(item?.config?.profit_margin));
    if (!isNaN(rawCost) && !isNaN(marginVal) && marginVal > 0) {
      const calculatedPrice = rawCost * (1 + marginVal / 100);
      setPrice(calculatedPrice);
    } else {
      setPrice(item?.price || '');
    }
  };

  const handleChangeMargin = (val) => {
    setProfitMargin(val);
    const marginVal = val !== '' ? parseFloat(val) : (item && parseFloat(item?.config?.profit_margin));
    const costVal = parseFloat(cost);
    if (!isNaN(costVal) && !isNaN(marginVal) && marginVal > 0) {
      const calculatedPrice = costVal * (1 + marginVal / 100);
      setPrice(calculatedPrice);
    } else {
      setPrice(item?.price || '');
    }
  };

  const handleChangeRate = (val) => {
    if (quantity === 0 || item === null) return;

    const numVal = Number(val || 0);
    const numQty = Number(quantity || 0);
    const base = numVal * numQty;
    let newTax = Number(tax || 0);

    if (taxPercentage !== '' && taxPercentage !== null && !isNaN(Number(taxPercentage))) {
      newTax = (Number(taxPercentage) * base) / 100;
      setTax(newTax);
    }
    const rawTotal = base + newTax;

    setCost(val);
    setTotalAmount(rawTotal);

    const marginVal = profitMargin !== '' ? parseFloat(profitMargin) : (item && parseFloat(item?.config?.profit_margin));
    const costVal = parseFloat(val);
    if (!isNaN(costVal) && !isNaN(marginVal) && marginVal > 0) {
      const calculatedPrice = costVal * (1 + marginVal / 100);
      setPrice(calculatedPrice);
    } else {
      setPrice(item?.price || '');
    }
  };

  const handleChangeQty = (val) => {
    if (item === null) return;
    setQuantity(val);
    setStock(prevStock + Number(val));

    const qty = Number(val) || 0;
    const base = qty * Number(cost || 0);
    let newTax = Number(tax || 0);

    if (taxPercentage !== '' && taxPercentage !== null && !isNaN(Number(taxPercentage))) {
      newTax = (Number(taxPercentage) * base) / 100;
      setTax(newTax);
    }
    const rawTotal = base + newTax;

    setTotalAmount(rawTotal);
  };

  const handleChangeTaxAmount = (val) => {
    if (item === null) return;
    const amount = Number(val) || 0;
    const base = Number(cost || 0) * Number(quantity || 0);

    const calculatedTaxPercentage = base > 0 ? (amount / base) * 100 : 0;

    setTax(val);
    setTaxPercentage(calculatedTaxPercentage);
    setTotalAmount(base + amount);
  };

  const handleChangeTaxPercentage = (val) => {
    if (item === null) return;
    const percent = Number(val) || 0;
    const base = Number(cost || 0) * Number(quantity || 0);

    const calculatedTax = (percent * base) / 100;

    setTaxPercentage(val);
    setTax(calculatedTax);
    setTotalAmount(base + calculatedTax);
  };

  const addItem = () => {
    let data = {
      plu: item.plu,
      item: {
        ...item,
        price: Number(price),
        config: {
          ...(item.config || {}),
          profit_margin: Number(profitMargin) || 0,
        },
      },
      rate: Number(cost),
      price: Number(price),
      profit_margin: Number(profitMargin) || 0,
      quantity: Number(quantity),
      tax: Number(tax),
      payable: Number(totalAmount),
      stock: Number(stock),
      taxPercentage: Number(taxPercentage),
    };
    if (editPurchaseItemId) {
      data.id = editPurchaseItemId;
    }
    if (editPurchaseItemId === null) {
      data = {
        ...data,
        purchase: editData.id,
        new_item: true,
      };
    } else {
      const updatingItem = (editData?.items || []).find((itm) => itm.id === editPurchaseItemId);
      data = {
        ...updatingItem,
        ...data,
      };
    }
    setPurchasedItems([...purchasedItems, data]);
    resetItemFields();
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  };

  const updateLocalInventory = useCallback(
    (updatedPurchaseItems) => {
      if (!updatedPurchaseItems) return;

      setProducts((prevProducts) => {
        if (!prevProducts) return prevProducts;

        const updatedItemsMap = new Map();
        updatedPurchaseItems.forEach((pi) => {
          if (pi.item && pi.item.id) {
            updatedItemsMap.set(pi.item.id, pi.item);
          }
        });

        let hasChanges = false;
        const newProducts = prevProducts.map((p) => {
          const updatedItem = updatedItemsMap.get(p.id);
          if (updatedItem) {
            hasChanges = true;
            return { ...p, ...updatedItem };
          }
          return p;
        });

        return hasChanges ? newProducts : prevProducts;
      });
    },
    [setProducts]
  );

  const handleKeyDown = (e, nextRef, prevRef) => {
    if (e.key === 'Enter') {
      if (itemPlu !== '' && itemName !== '' && cost !== '') {
        addItem();
      }
    } else if (e.key === 'ArrowRight') {
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    } else if (e.key === 'ArrowLeft') {
      if (prevRef && prevRef.current) {
        prevRef.current.focus();
      }
    }
  };

  const handleAddPurchase = async () => {
    setLoadingIndicator(true);
    try {
      const data = {
        vendor_id: vendor?.id,
        warehouse_id: warehouse || null,
        invoice_no: invoiceNumber,
        bill_date: DateTimeHelpers.convertDateToDMY(billDate),
        payment_date: DateTimeHelpers.convertDateToDMY(paymentDate),
        date: DateTimeHelpers.getTimezoneOffsetDate(purchaseDate).toISOString(),
        items: purchasedItems.map((pi) => ({ ...pi, item_id: pi.item.id })),
        payment_modes: (paymentModes || []).map((pm) => ({ ...pm, amount: Number(pm.amount || 0) })),
        paid_amount: (paymentModes || []).reduce((sum, pm) => sum + Number(pm.amount || 0), 0),
        vendor_name_old: vendorNameOld,
        batch_name: batchName,
      };
      const resp = await PurchaseService.addPurchase(data);
      if (resp && resp.items) {
        updateLocalInventory(resp.items);
      }
      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchEnabledItems = purchasedItems.some((pi) => {
        const itemObj = pi.item;
        if (itemObj?.is_batch_enabled) return true;
        const subItems = itemObj?.details?.sub_items || [];
        return subItems.some((si) => {
          const subItemObj = (items || []).find((p) => p.id === (si.item_id || si.id));
          return subItemObj?.is_batch_enabled;
        });
      });
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);
      const promises = [];
      promises.push(getProducts({ useCache: false }));

      if (hasBatchEnabledItems && isPremium) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }

      await Promise.all(promises);

      postAlertMessage({
        text: tOperations('Purchase Added successfully'),
        type: 'success',
      });
      history.push(ROUTES.PURCHASE_HISTORY);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const dltItem = (item) => {
    if (item.hasOwnProperty('id')) {
      setRemovedItems((old) => [...old, item.id]);
    }
    setPurchasedItems(purchasedItems.filter((itm) => itm !== item));
  };

  const handleEditItem = (purchaseItem) => {
    setEditPurchaseItemId(purchaseItem?.id);
    setItem(purchaseItem.item);
    setItemName(purchaseItem.item?.name);
    setItemPlu(purchaseItem?.plu);
    setQuantity(purchaseItem?.quantity);
    setCost(purchaseItem?.rate);
    setPrice(purchaseItem?.item.price);
    setProfitMargin(purchaseItem?.profit_margin || purchaseItem?.item?.config?.profit_margin || '');
    setTax(purchaseItem?.tax);
    setTotalAmount(purchaseItem?.payable);
    const calculatedTaxPercentage =
      purchaseItem.taxPercentage ??
      (Number(purchaseItem.rate) > 0
        ? (Number(purchaseItem.tax) / (Number(purchaseItem.rate) * Number(purchaseItem.quantity))) * 100
        : 0);
    setTaxPercentage(calculatedTaxPercentage);
    setPurchasedItems(purchasedItems.filter((itm) => itm !== purchaseItem));
    setStock(editData?.id ? purchaseItem.item.stock : purchaseItem.item.stock + Number(purchaseItem?.quantity));
    setPrevStock(editData?.id ? purchaseItem.item.stock - Number(purchaseItem?.quantity) : purchaseItem.item.stock);
  };

  const handleEditPurchase = async (forceUpdate = false, dataOverride = null) => {
    setLoadingIndicator(true);
    try {
      let data = dataOverride;
      if (!data) {
        const _items = purchasedItems.map((pi) => ({
          ...pi,
          item_id: pi.item.id,
        }));
        const olditems = _items.filter((purchaseItem) => !purchaseItem.new_item);
        const newItems = _items.filter((purchaseItem) => purchaseItem.new_item);
        data = {
          ...editData,
          vendor_id: vendor?.id,
          warehouse_id: warehouse || null,
          invoice_no: invoiceNumber,
          bill_date: DateTimeHelpers.convertDateToDMY(billDate),
          payment_date: DateTimeHelpers.convertDateToDMY(paymentDate),
          date: DateTimeHelpers.getTimezoneOffsetDate(purchaseDate).toISOString(),
          items: olditems,
          new_items: newItems,
          remove_items: removedItems,
          vendor_name_old: vendorNameOld,
          paid_amount: Number(paidAmount || 0),
          batch_name: batchName,
        };
      }

      if (forceUpdate) {
        data.force_update = true;
      }

      const resp = await PurchaseService.updatePurchase(data, id);
      if (resp && resp.items) {
        updateLocalInventory(resp.items);
      }

      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchInPurchased = purchasedItems.some((pi) => pi.item?.is_batch_enabled);
      const hasBatchInRemoved = Array.isArray(removedItems) && removedItems.some((removedId) => {
        const origItem = (editData?.items || []).find((pi) => pi.id === removedId);
        return origItem?.item?.is_batch_enabled;
      });
      const hasBatchEnabled = hasBatchInPurchased || hasBatchInRemoved;
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);

      const promises = [getProducts({ useCache: false })];
      if (isPremium && hasBatchEnabled) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }
      await Promise.all(promises);

      postAlertMessage({
        text: tOperations('Purchase updated successfully'),
        type: 'success',
      });
      setLoadingIndicator(false);
      history.push(ROUTES.PURCHASE_HISTORY);
    } catch (err) {
      setLoadingIndicator(false);
      if (err.status_code === ERROR_CODES.BATCH_RECORDS_EXIST) {
        const _items = purchasedItems.map((pi) => ({
          ...pi,
          item_id: pi.item.id,
        }));
        const olditems = _items.filter((purchaseItem) => !purchaseItem.new_item);
        const newItems = _items.filter((purchaseItem) => purchaseItem.new_item);
        const currentData = {
          ...editData,
          vendor_id: vendor?.id,
          warehouse_id: warehouse || null,
          invoice_no: invoiceNumber,
          bill_date: DateTimeHelpers.convertDateToDMY(billDate),
          payment_date: DateTimeHelpers.convertDateToDMY(paymentDate),
          date: DateTimeHelpers.getTimezoneOffsetDate(purchaseDate).toISOString(),
          items: olditems,
          new_items: newItems,
          remove_items: removedItems,
          vendor_name_old: vendorNameOld,
          paid_amount: Number(paidAmount || 0),
          batch_name: batchName,
        };
        setPendingUpdateData(currentData);
        setShowForceUpdateDialog(true);
      } else {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
    }
  };

  const handleConfirmForceUpdate = async () => {
    setShowForceUpdateDialog(false);
    if (pendingUpdateData) {
      await handleEditPurchase(true, pendingUpdateData);
    }
  };

  const onSavePurchase = () => {
    if (editData.id) {
      handleEditPurchase();
    } else {
      handleAddPurchase();
    }
  };

  const _getPurchaseSummary = (items) => {
    let totalPayable = 0;
    let totalTax = 0;
    let totalQty = 0;
    items.forEach((item) => {
      totalPayable += item.rate * item.quantity + item.tax;
      totalTax += item.tax;
      totalQty += item.quantity;
    });

    return {
      totalPayable: ShopsHelper.getAmountFormatted(shop, totalPayable),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalQty: totalQty,
    };
  };

  const summary = _getPurchaseSummary(purchasedItems);

  const filteredPurchasedItems = purchasedItems.filter((purchasedItem) => {
    if (!searchText) return true;

    const search = searchText.toLowerCase();

    return (
      String(purchasedItem.plu).toLowerCase().includes(search) ||
      purchasedItem.item?.name?.toLowerCase().includes(search) ||
      String(purchasedItem.item?.ean).toLowerCase().includes(search)
    );
  });

  const color = vendor ? (vendor.balance < 0 ? 'red' : vendor.balance > 0 ? 'green' : 'inherit') : 'inherit';

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  const filteredItems = (items || []).filter((item) => item?.type === 'INVENTORY');

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title} style={{ marginBottom: 0 }}>
          {tOperations('Purchase')}
          <span className={styles.menuTitle}>{tOperations('Management')}</span>
        </h2>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec} style={{ marginBottom: '16px' }}>
          <div className={styles.headTitle} style={{ paddingBottom: 0 }}>
            <h2 className={styles.subTitle} style={{ paddingBottom: 5 }}>
              {tOperations('New Purchase')}
            </h2>
          </div>
        </div>
        <Grid container spacing={3}>
          {/* Column 1: Vendor, Vendor Balance, Invoice Number */}
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                {shop && featureIsExpired(featureValidity) ? (
                  <TextField
                    fullWidth
                    label={tOperations('Vendor')}
                    value={vendorNameOld || ''}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      setVendorNameOld(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e0' },
                        '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                      },
                      '& input': {
                        paddingTop: '10.5px',
                        paddingBottom: '10.5px',
                        fontSize: '14px',
                      },
                    }}
                  />
                ) : (
                  <TextField
                    select
                    fullWidth
                    label={tOperations('Vendor')}
                    value={vendor?.id || 0}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e0' },
                        '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                      },
                      '& .MuiSelect-select': {
                        paddingTop: '10.5px',
                        paddingBottom: '10.5px',
                        fontSize: '14px',
                      },
                    }}
                    onChange={(e) => {
                      const selectedId = Number(e.target.value);
                      const selectedVendor = vendors.find((v) => v.id === selectedId);
                      setVendor(selectedVendor ?? null);
                    }}
                  >
                    <MenuItem value={0}>
                      <em>{tOperations('No Vendor')}</em>
                    </MenuItem>
                    {vendors.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.name}
                      </MenuItem>
                    ))}
                    <CustomMenuItem onClick={openCreateVendorDialog}>
                      <AddCircleIcon fontSize="small" style={{ marginLeft: 5 }} />
                      <p className={styles.normalText} style={{ fontSize: '12px' }}>
                        {tOperations('New Vendor')}
                      </p>
                    </CustomMenuItem>
                  </TextField>
                )}
              </Grid>
              <Grid item>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={tOperations('Vendor Balance')}
                  InputProps={{ readOnly: true, style: { color } }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  value={
                    loadingIndicator && !vendor
                      ? ''
                      : vendor
                      ? ShopsHelper.getAmountFormatted(shop, vendor.balance)
                      : '—'
                  }
                />
                {loadingIndicator && !vendor && (
                  <Skeleton variant="text" width="60%" animation="wave" style={{ marginTop: 4 }} />
                )}
              </Grid>
              <Grid item>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={tCommon('Invoice Number')}
                  value={invoiceNumber}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  onChange={(e) => {
                    setInvoiceNumber(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Column 2: All Dates */}
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    format="dd/MM/yyyy hh:mm a"
                    value={purchaseDate}
                    label={tCommon('Purchase Date')}
                    onChange={(date) => {
                      setPurchaseDate(date);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        margin: "none",
                        error: false,
                        id: "date-purchase",
                        InputLabelProps: { shrink: true },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e0' },
                            '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                          },
                          '& input': {
                            paddingTop: '10.5px',
                            paddingBottom: '10.5px',
                            fontSize: '14px',
                          },
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    value={billDate}
                    label={tCommon('Bill Date')}
                    onChange={(date) => {
                      setBillDate(date);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        margin: "none",
                        id: "date-bill",
                        InputLabelProps: { shrink: true },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e0' },
                            '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                          },
                          '& input': {
                            paddingTop: '10.5px',
                            paddingBottom: '10.5px',
                            fontSize: '14px',
                          },
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    value={paymentDate}
                    label={tCommon('Payment Date')}
                    onChange={(date) => {
                      setPaymentDate(date);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        margin: "none",
                        error: false,
                        id: "date-payment",
                        InputLabelProps: { shrink: true },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e0' },
                            '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                          },
                          '& input': {
                            paddingTop: '10.5px',
                            paddingBottom: '10.5px',
                            fontSize: '14px',
                          },
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 3: Others */}
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={tOperations('Batch Name')}
                  value={batchName}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  onChange={(e) => {
                    setBatchName(e.target.value);
                  }}
                />
              </Grid>
              {!featureIsExpired(shop?.warehouse_validity) && shop?.config?.is_warehouse_enabled && (
                <Grid item>
                  <TextField
                    select
                    fullWidth
                    label={tOperations('Warehouse')}
                    value={warehouse || ''}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e0' },
                        '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                      },
                      '& .MuiSelect-select': {
                        paddingTop: '10.5px',
                        paddingBottom: '10.5px',
                        fontSize: '14px',
                      },
                    }}
                    onChange={(e) => {
                      setWarehouse(e.target.value);
                    }}
                  >
                    {warehouses.map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={tOperations('Search Items')}
                  placeholder={tOperations('Search products')}
                  value={searchText}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon style={{ fontSize: '20px', color: '#718096' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>

      <div className={styles.changeable} style={{ boxShadow: 'none', padding: '0' }}>
        <div className={styles.itemEntryRow}>
          <div className={styles.itemEntryFieldPlu}>
            <Autocomplete
              value={item}
              onChange={(event, newItem) => {
                setItem(newItem);
                setItemName(newItem.name || '');
                setCost(newItem?.cost || '');
                const margin = newItem?.config?.profit_margin || '';
                setProfitMargin(margin);
                const costVal = parseFloat(newItem?.cost || 0);
                const marginVal = parseFloat(margin);
                if (!isNaN(costVal) && !isNaN(marginVal) && marginVal > 0) {
                  const calculatedPrice = costVal * (1 + marginVal / 100);
                  setPrice(ShopsHelper.getAmountFormatted(shop, calculatedPrice));
                } else {
                  setPrice(newItem?.price || '');
                }
                setStock(newItem?.stock || 0);
                setPrevStock(newItem?.stock || 0);
                setTotalAmount(newItem?.cost * quantity);
              }}
              disableClearable
              inputValue={itemPlu}
              onInputChange={(e, value) => {
                setItemPlu(value);
              }}
              getOptionLabel={(option) => (typeof option === 'object' && option !== null ? String(option.plu) : '')}
              filterOptions={(options, { inputValue }) =>
                options.filter(
                  (option) =>
                    String(option.plu).includes(inputValue) ||
                    (option.ean && option.ean.toLowerCase().includes(inputValue.toLowerCase()))
                )
              }
              options={filteredItems}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PLU"
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputRef={pluInputRef}
                  onKeyDown={(e) => handleKeyDown(e, nameInputRef, null)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>
          <div className={styles.itemEntryFieldName}>
            <Autocomplete
              value={item}
              onChange={(event, newItem) => {
                setItem(newItem);
                setItemPlu(newItem?.plu ? String(newItem.plu) : '');
                setCost(newItem?.cost || '');
                const margin = newItem?.config?.profit_margin || '';
                setProfitMargin(margin);
                const costVal = parseFloat(newItem?.cost || 0);
                const marginVal = parseFloat(margin);
                if (!isNaN(costVal) && !isNaN(marginVal) && marginVal > 0) {
                  const calculatedPrice = costVal * (1 + marginVal / 100);
                  setPrice(ShopsHelper.getAmountFormatted(shop, calculatedPrice));
                } else {
                  setPrice(newItem?.price || '');
                }
                setStock(newItem?.stock || 0);
                setPrevStock(newItem?.stock || 0);
                setTotalAmount(newItem?.cost * quantity);
              }}
              disableClearable
              inputValue={itemName}
              onInputChange={(event, newInputValue) => {
                setItemName(newInputValue);
              }}
              options={filteredItems}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
              filterOptions={(options, { inputValue }) =>
                options.filter(
                  (option) =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (option.ean && option.ean.toLowerCase().includes(inputValue.toLowerCase()))
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tOperations('Item Search')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder={tOperations('Name or Barcode')}
                  inputRef={nameInputRef}
                  onKeyDown={(e) => handleKeyDown(e, costInputRef, pluInputRef)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e0' },
                      '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                    },
                    '& input': {
                      paddingTop: '10.5px',
                      paddingBottom: '10.5px',
                      fontSize: '14px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={editPurchaseItemId}
                />
              )}
            />
          </div>
          <div className={styles.itemEntryField}>
            <NumberField
              label={tCommon('Cost')}
              value={cost}
              onChange={(e) => handleChangeRate(e.target.value)}
              onBlur={() => setCost(cost === '' ? '' : ShopsHelper.getAmountFormatted(shop, cost))}
              inputRef={costInputRef}
              onKeyDown={(e) => handleKeyDown(e, marginInputRef, nameInputRef)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.itemEntryField}>
            <NumberField
              label={tCommon('Margin (%)')}
              value={profitMargin}
              onChange={(e) => handleChangeMargin(e.target.value)}
              inputRef={marginInputRef}
              onKeyDown={(e) => handleKeyDown(e, priceInputRef, costInputRef)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.itemEntryField}>
            <NumberField
              label={tCommon('Price')}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              onBlur={() => setPrice(price === '' ? '' : ShopsHelper.getAmountFormatted(shop, price))}
              inputRef={priceInputRef}
              onKeyDown={(e) => handleKeyDown(e, qtyInputRef, marginInputRef)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.itemEntryFieldSmall}>
            <NumberField
              label={tCommon('Qty')}
              value={quantity}
              onChange={(e) => handleChangeQty(e.target.value)}
              inputRef={qtyInputRef}
              onKeyDown={(e) => handleKeyDown(e, taxPercentInputRef, priceInputRef)}
              InputLabelProps={{ shrink: true }}
              helperText={
                item && <span style={{ fontSize: '10px', color: '#718096', fontWeight: 500 }}>Stock: {stock}</span>
              }
              FormHelperTextProps={{
                style: { position: 'absolute', top: '100%', margin: 0, marginTop: '2px', whiteSpace: 'nowrap' },
              }}
            />
          </div>
          <div className={styles.itemEntryFieldSmall}>
            <NumberField
              label={tCommon('Tax(%)')}
              value={taxPercentage}
              onChange={(e) => handleChangeTaxPercentage(e.target.value)}
              onBlur={() => setTaxPercentage(taxPercentage === '' ? '' : ShopsHelper.getAmountFormatted(shop, taxPercentage))}
              inputRef={taxPercentInputRef}
              onKeyDown={(e) => handleKeyDown(e, taxAmountInputRef, qtyInputRef)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.itemEntryField}>
            <NumberField
              label={tCommon('Tax')}
              value={tax}
              onChange={(e) => handleChangeTaxAmount(e.target.value)}
              onBlur={() => setTax(tax === '' ? '' : ShopsHelper.getAmountFormatted(shop, tax))}
              inputRef={taxAmountInputRef}
              onKeyDown={(e) => handleKeyDown(e, totalInputRef, taxPercentInputRef)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.itemEntryFieldTotal}>
            <NumberField
              label={tCommon('Total')}
              value={totalAmount}
              onChange={(e) => handleChangePayable(e.target.value)}
              onBlur={() => setTotalAmount(totalAmount === '' ? '' : ShopsHelper.getAmountFormatted(shop, totalAmount))}
              inputRef={totalInputRef}
              onKeyDown={(e) => handleKeyDown(e, null, taxAmountInputRef)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.itemEntryAction}>
            <Tooltip title={tCommon('Add Items')}>
              <span>
                <IconButton disabled={itemPlu === '' || itemName === '' || cost === ''} onClick={addItem} size="small">
                  <LibraryAddIcon color="primary" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <TableContainer style={{ marginTop: 4, overflowX: 'auto' }} component={Paper}>
        <Table aria-label="customized table" sx={{ minWidth: 'max-content' }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '100px' }} className={styles.columnCell}>
                {tCommon('PLU')}
              </TableCell>
              <TableCell style={{ width: '250px' }} className={styles.nameColumn}>
                {tCommon('Item Name')}
              </TableCell>
              <TableCell style={{ width: '100px' }} className={styles.columnCell}>
                {tCommon('Cost')}
              </TableCell>
              <TableCell style={{ width: '100px' }} className={styles.columnCell}>
                {tCommon('Margin (%)')}
              </TableCell>
              <TableCell style={{ width: '100px' }} className={styles.columnCell}>
                {tCommon('Unit Price')}
              </TableCell>
              <TableCell style={{ width: '80px' }} className={styles.columnCell}>
                {tCommon('Qty')}
              </TableCell>
              <TableCell style={{ width: '80px' }} className={styles.columnCell}>
                {tCommon('Stock')}
              </TableCell>
              <TableCell style={{ width: '80px' }} className={styles.columnCell}>
                {tCommon('Tax(%)')}
              </TableCell>
              <TableCell style={{ width: '100px' }} className={styles.columnCell}>
                {tCommon('Tax')}
              </TableCell>
              <TableCell style={{ width: '120px' }} className={styles.columnCell}>
                {tCommon('Total')}
              </TableCell>
              <TableCell style={{ width: '50px' }} className={styles.columnCell}>
                {tCommon('Act')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(loadingIndicator || !items) &&
              [...Array(3)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                </TableRow>
              ))}
            {filteredPurchasedItems.length === 0 && !loadingIndicator && items && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  {tOperations('No matching items found')}
                </TableCell>
              </TableRow>
            )}

            {filteredPurchasedItems &&
              filteredPurchasedItems.map((purchasedItem) => (
                <TableRow key={purchasedItem.id}>
                  <TableCell align="center">{purchasedItem.plu}</TableCell>
                  <TableCell>{purchasedItem.item.name}</TableCell>
                  <TableCell align="center">{ShopsHelper.getAmountFormatted(shop, purchasedItem.rate)}</TableCell>
                  <TableCell align="center">{purchasedItem.profit_margin ? `${purchasedItem.profit_margin}%` : '0%'}</TableCell>
                  <TableCell align="center">{ShopsHelper.getAmountFormatted(shop, purchasedItem.item.price)}</TableCell>
                  <TableCell align="center">{purchasedItem.quantity}</TableCell>
                  <TableCell align="center">{purchasedItem.stock ?? purchasedItem.item.stock}</TableCell>
                  <TableCell align="center">
                    {purchasedItem.taxPercentage ??
                      (purchasedItem.rate > 0
                        ? ((purchasedItem.tax / (purchasedItem.rate * purchasedItem.quantity)) * 100).toFixed(2)
                        : 0)}
                    %
                  </TableCell>
                  <TableCell align="center">{ShopsHelper.getAmountFormatted(shop, purchasedItem.tax)}</TableCell>
                  <TableCell align="center">
                    {ShopsHelper.getAmountFormatted(
                      shop,
                      Number(purchasedItem.rate) * Number(purchasedItem.quantity) + Number(purchasedItem.tax)
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <IconButton onClick={() => dltItem(purchasedItem)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleEditItem(purchasedItem)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            <TableRow key={'total'} style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
              <TableCell></TableCell>
              <TableCell align="right" style={{ fontWeight: 600, color: '#4a5568' }}>
                {tCommon('Total')}
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell align="center" style={{ fontWeight: 700, color: '#2d3748' }}>
                {summary.totalQty}
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell align="center" style={{ fontWeight: 700, color: '#2d3748' }}>
                {summary.totalTax}
              </TableCell>
              <TableCell align="center" style={{ fontWeight: 700, color: '#2d3748' }}>
                {summary.totalPayable}
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {!editData?.id && (
        <div className={styles.changeable} style={{ marginTop: 12 }}>
          <h3 className={styles.subTitle} style={{ marginBottom: 16 }}>
            {tOperations('Payment Information')}
          </h3>
          <TableContainer component={Paper}>
            <Table aria-label="customized table">
              <TableHead>
                <TableRow>
                  {(paymentModes || []).map((pm) => (
                    <TableCell key={pm.mode_id}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <NumberField
                          label={pm.name}
                          value={pm.amount || ''}
                          onChange={(e) => {
                            const updatedAmount = e.target.value;
                            setPaymentModes((prev) =>
                              prev.map((item) =>
                                item.mode_id === pm.mode_id ? { ...item, amount: updatedAmount } : item
                              )
                            );
                          }}
                          onBlur={() => {
                            setPaymentModes((prev) =>
                              prev.map((item) =>
                                item.mode_id === pm.mode_id ? { ...item, amount: pm.amount === '' ? '' : ShopsHelper.getAmountFormatted(shop, pm.amount) } : item
                              )
                            );
                          }}
                        />
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          className={styles.payFullBtn}
                          title={tOperations('Pay Full')}
                          onClick={() => {
                            const payableAmount = Number(summary.totalPayable);
                            setPaymentModes((prev) =>
                              prev.map((item) =>
                                item.mode_id === pm.mode_id
                                  ? { ...item, amount: payableAmount }
                                  : { ...item, amount: 0 }
                              )
                            );
                          }}
                        >
                          {tOperations('Payment In Full')}
                        </Button>
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        </div>
      )}
      <Grid container className={styles.submitSection} justifyContent="flex-end">
        <Link to={ROUTES.PURCHASE_HISTORY} className={styles.link}>
          <Button variant="contained" color="secondary" className={styles.closeBtn}>
            {tCommon('Cancel')}
          </Button>
        </Link>
        <Button
          variant="contained"
          style={{
            backgroundColor: '#00a65a',
            borderRadius: '8px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
          disabled={
            invoiceNumber === '' ||
            billDate === '' ||
            purchaseDate === '' ||
            purchasedItems.length === 0 ||
            loadingIndicator
          }
          onClick={onSavePurchase}
        >
          {editData.id ? 'Update Purchase' : 'Save Purchase'}
        </Button>
      </Grid>
      {isShowAddVendonDialog && <NewVendorDialog onClose={closeCreateVendorDialog} onSuccess={onCreateVendor} />}
      <ConfirmationDialog
        open={isShowForceUpdateDialog}
        handleClose={() => setShowForceUpdateDialog(false)}
        handleConfirm={handleConfirmForceUpdate}
        title={tOperations('Confirm Force Update')}
        message={tOperations(
          'Inventory Conflict: Sales already exist for items in this batch. A force update is required to proceed. Do you wish to continue?'
        )}
        confirmText={tCommon('Yes')}
        cancelText={tCommon('Cancel')}
        loading={loadingIndicator}
      />
    </div>
  );
}

export default withConsoleBase(AddPurchase);
